import React from 'react';
import PopupState, { bindTrigger, bindPopover } from 'material-ui-popup-state';
import {
  Box,
  Button,
  Drawer,
  Popover,
  Typography,
  Badge,
  IconButton,
  Stack,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemButton from '@mui/material/ListItemButton';
import Chip from '@mui/material/Chip';
import NotificationsIcon from '@mui/icons-material/Notifications';
import RefreshIcon from '@mui/icons-material/Refresh';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import Divider from '@mui/material/Divider';

// notiTyp 별 칩 레이블/색상. bito 알림 소스는 수주품의서(FORECAST)·근태(HR_LEAVE) 뿐이라
// KSM의 TECH/영업 2분류 대신 실제 값에 맞춘다. 새 notiTyp이 추가되면 여기만 더하면 된다.
const NOTI_TYP_LABEL = {
  FORECAST: { label: '영업', color: 'warning' },
  HR_LEAVE: { label: '근태', color: 'info' },
};

const NotiBox = ({ noties, notiCount, onConfirmNoties, onConfirmNotiAll, onRefresh }) => {
  const theme = useTheme();
  // 폭이 좁은 화면에서는 Popover(600px 고정폭) 대신 바텀 드로어로 전체화면 폭을 쓴다.
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const safeNoties = noties || [];

  const renderContent = (popupState) => (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: { xs: '100vw', sm: 420, md: 600 },
          maxWidth: { xs: '100vw', sm: 420, md: 600 },
          maxHeight: { xs: '75dvh', md: 400 },
          overflowY: 'auto',
        }}
      >
        <List sx={{ width: '100%', minWidth: 0 }}>
          {safeNoties.length === 0 && (
            <ListItem alignItems="center">확인할 알림이 없습니다</ListItem>
          )}

          {safeNoties.length > 0 &&
            safeNoties.map((noti, index) => {
              const typInfo = NOTI_TYP_LABEL[noti.notiTyp] || { label: noti.notiTyp || '알림', color: 'default' };
              return (
                <React.Fragment key={index}>
                  <ListItem dense divider disablePadding>
                    <ListItemButton
                      onClick={() => {
                        popupState.close();
                        onConfirmNoties(noti);
                      }}
                    >
                      <ListItemText
                        secondary={
                          <React.Fragment>
                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'flex-start',
                                alignItems: 'center',
                                gap: 1,
                                minWidth: 0,
                              }}
                            >
                              {noti.viewYn === 'N' && (
                                <Badge
                                  badgeContent={noti.viewYn}
                                  size="small"
                                  color="error"
                                />
                              )}
                              <Chip
                                label={typInfo.label}
                                size="small"
                                color={typInfo.color}
                              />

                              <Typography
                                component="div"
                                variant="body2"
                                align="left"
                                sx={{
                                  flex: 1,
                                  minWidth: 0,
                                  wordBreak: 'break-word',
                                }}
                              >
                                {noti.notiTxt}
                              </Typography>
                            </Box>
                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                                alignItems: 'center',
                                gap: 1,
                                mt: 0.5,
                              }}
                            >
                              <Typography
                                component="div"
                                variant="body2"
                                color="text.disabled"
                                align="left"
                              >
                                {noti.crtDt}
                              </Typography>
                            </Box>
                          </React.Fragment>
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                </React.Fragment>
              );
            })}
        </List>
      </Box>
      <Divider />
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{ px: 1.5, py: 1 }}
      >
        <Button
          size="small"
          color="inherit"
          startIcon={<RefreshIcon fontSize="small" />}
          onClick={onRefresh}
        >
          새로고침
        </Button>

        <Box sx={{ flexGrow: 1 }} />

        {/*
          예전 이름은 "전체 삭제" 였는데 실제로는 지우지 않고 모두 읽음 처리만 한다.
          지운다고 적혀 있으면 누르기가 무섭고, 실제 동작과도 다르다.
        */}
        <Button
          size="small"
          color="inherit"
          startIcon={<DoneAllIcon fontSize="small" />}
          disabled={safeNoties.length === 0}
          onClick={() => {
            popupState.close();
            onConfirmNotiAll();
          }}
        >
          모두 읽음
        </Button>
        <Button size="small" variant="contained" onClick={popupState.close}>
          닫기
        </Button>
      </Stack>
    </>
  );

  return (
    <PopupState variant="popover" popupId="NotiPopover">
      {(popupState) => (
        <Box>
          <IconButton color="inherit" {...bindTrigger(popupState)}>
            <Badge badgeContent={notiCount} color="error">
              <NotificationsIcon />
            </Badge>
          </IconButton>

          {isMobile ? (
            <Drawer
              anchor="bottom"
              open={popupState.isOpen}
              onClose={popupState.close}
              PaperProps={{
                sx: {
                  borderTopLeftRadius: 8,
                  borderTopRightRadius: 8,
                  overflow: 'hidden',
                },
              }}
            >
              {renderContent(popupState)}
            </Drawer>
          ) : (
            <Popover
              {...bindPopover(popupState)}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
            >
              {renderContent(popupState)}
            </Popover>
          )}
        </Box>
      )}
    </PopupState>
  );
};

export default NotiBox;
